import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import axios from 'axios';
import './CsvUploader.css';

const createApiInstance = (token, backendKey) => {
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      'x-api-key': backendKey,
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });
};

const CsvUploader = ({ accessToken }) => {
  const BACKEND_KEY = process.env.REACT_APP_BACKEND_KEY;

  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');

  const api = useMemo(() => createApiInstance(accessToken, BACKEND_KEY), [accessToken, BACKEND_KEY]);

  // NEW: navigation + file input ref
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const resetFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setCsvData([]);
    // keep validation errors visible unless explicitly resetting:
    // setValidationErrors([]);
  };

  const validateRow = (row, index) => {
    const errors = [];
    const numericFields = ['size_l', 'size_u', 'size_d'];
    const requiredFields = ['sitename', 'company', 'category', 'country', 'coordinates'];

    requiredFields.forEach(field => {
      if (!row[field] || String(row[field]).trim() === '') {
        errors.push(`Row ${index + 1}: Missing value for "${field}"`);
      }
    });

    numericFields.forEach(field => {
      if (row[field] && isNaN(parseFloat(row[field]))) {
        errors.push(`Row ${index + 1}: "${field}" must be a valid number`);
      }
    });

    // confidence: optional; if present must be 1, 2, or 3
    if (row.confidence !== undefined && row.confidence !== null && String(row.confidence).trim() !== '') {
      const c = Number.parseInt(row.confidence, 10);
      if (![1, 2, 3].includes(c)) {
        errors.push(`Row ${index + 1}: "confidence" must be 1, 2, or 3 (leave blank to default to 0)`);
      }
    }

    try {
      const coords = JSON.parse(row.coordinates);
      if (
        !Array.isArray(coords) ||
        coords.length !== 2 ||
        coords.some(val => isNaN(parseFloat(val)))
      ) {
        errors.push(`Row ${index + 1}: "coordinates" must be [longitude, latitude]`);
      }
    } catch {
      errors.push(`Row ${index + 1}: Invalid JSON in "coordinates"`);
    }

    return errors;
  };

  const normaliseRow = (row) => {
    const { id, geom, ...rest } = row;

    if (rest.confidence === undefined || rest.confidence === null || String(rest.confidence).trim() === '') {
      delete rest.confidence; // backend defaults to 0
    } else {
      rest.confidence = String(rest.confidence).trim();
    }

    return rest;
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const cleanedData = results.data.map(normaliseRow);

        const allErrors = cleanedData.flatMap((row, i) => validateRow(row, i));
        setValidationErrors(allErrors);

        if (allErrors.length === 0) {
          setCsvData(cleanedData);
          setStatusMessage(`✅ Valid CSV. ${cleanedData.length} rows ready.`);
        } else {
          setCsvData([]);
          setStatusMessage('❌ Validation errors found. The file has been cleared.');
          resetFileInput(); // NEW: deselect and empty on validation failure
        }
      },
      error: (err) => {
        setStatusMessage(`❌ CSV parsing error: ${err.message}. The file has been cleared.`);
        setCsvData([]);
        setValidationErrors([`CSV parsing error: ${err.message}`]);
        resetFileInput(); // NEW: deselect and empty on parse failure
      },
    });
  };

  const handleUpload = async () => {
    try {
      await api.post('/api/upload-csv', { data: csvData });
      setStatusMessage('✅ Upload successful');
      setCsvData([]);
      setValidationErrors([]);
      resetFileInput(); // optional: clear file after success
    } catch (error) {
      const serverMsg = error.response?.data?.message || error.response?.data || error.message;
      setStatusMessage(`❌ Upload failed: ${serverMsg}. The file has been cleared.`);
      // keep validation errors unchanged; add a server error for visibility
      setValidationErrors(prev => [...prev, `Server error: ${serverMsg}`]);
      resetFileInput(); // NEW: deselect and empty on upload failure
    }
  };

  const handleClear = () => {
    setStatusMessage('ℹ️ Selection cleared.');
    setValidationErrors([]);
    resetFileInput();
  };

  return (
    <div className="csvuploader">
      <div className="csvuploader__header">
        <h2 className="csvuploader__title">Upload CSV</h2>
        <button
          type="button"
          className="csvuploader__home-btn"
          onClick={() => navigate('/')}
        >
          ⟵ Home
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFile}
        className="csvuploader__file"
      />

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          className="csvuploader__btn"
          onClick={handleUpload}
          disabled={csvData.length === 0 || validationErrors.length > 0}
        >
          Upload to Database
        </button>

        <button
          type="button"
          className="csvuploader__btn csvuploader__btn--secondary"
          onClick={handleClear}
        >
          Clear File
        </button>
      </div>


      {validationErrors.length > 0 && (
        <div className="csvuploader__errors">
          <strong>Validation errors:</strong>
          <ul>
            {validationErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CsvUploader;
